// ⚠️ FILE NÀY = TRANG ĐƠN SỈ (wholesale / bán buôn). type=2 trong API.
//    Đơn lẻ (type=1) ở `OrderListForm.js`. KHÔNG nhầm lẫn.
//    URL: /don-hang-ban-buon. UI label: "đơn bán buôn" / "đơn sỉ".
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import OrderFormPreview from './OrderFormPreview';
import GuideButton from './shared/GuideButton';
import { CungVeDichAPI } from '../services/API';
import { getFeatureConfig } from '../config/featureDisplayConfig';

const AutocompleteSelect = ({
  options,
  value,
  onChange,
  placeholder,
  displayField,
  searchFields = [],
  disabled = false,
  maxResults = 10
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options
    .filter(option => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return searchFields.some(field => {
        const fieldValue = option[field]?.toString().toLowerCase() || '';
        return fieldValue.includes(searchLower);
      });
    })
    .slice(0, maxResults);

  const getDisplayText = () => {
    if (!value) return '';
    const selected = options.find(opt => opt.id.toString() === value.toString());
    return selected ? displayField(selected) : '';
  };

  const handleSelect = (option) => {
    onChange(option.id.toString());
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-3 pr-8 py-2 text-base md:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Không tìm thấy kết quả</div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`px-3 py-2 cursor-pointer text-sm ${index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'text-gray-900 hover:bg-gray-50'}`}
              >
                {displayField(option)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const WholesaleOrderList = ({ userInfo, onLogout, token, totalPoints, accessToken, onRefreshPoints }) => {
  const navigate = useNavigate();
  const wholesaleConfig = getFeatureConfig('wholesale-order');
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewOrderId, setPreviewOrderId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Load customers
  useEffect(() => {
    const loadCustomers = async () => {
      if (!accessToken) return;
      try {
        const result = await CungVeDichAPI.getCustomers(accessToken, userInfo.id, 14);
        if (result.success && result.customers) {
          setCustomers(result.customers);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, [accessToken, userInfo.id]);

  // Helper function for price formatting
  const formatPrice = (value) => {
    if (!value || value === '' || isNaN(parseFloat(value))) return '0';
    return parseFloat(value).toLocaleString('vi-VN');
  };

  // Helper function for date formatting
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month} ${hours}:${minutes}`;
  };

  // Handle preview order
  const handlePreviewOrder = (orderId) => {
    setPreviewOrderId(orderId);
    setShowPreview(true);
  };

  // Handle delete order
  const handleDeleteOrder = async (orderId, customerName) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa đơn hàng bán buôn của "${customerName}"?`)) {
      return;
    }

    try {
      console.log('Deleting wholesale order:', orderId);
      const result = await CungVeDichAPI.deleteOrder(orderId, accessToken);

      if (result.success) {
        setMessage('Xóa đơn hàng bán buôn thành công');
        setMessageType('success');

        // Remove the deleted order from the list
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));

        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage('');
          setMessageType('');
        }, 3000);
      } else {
        setMessage('Không thể xóa đơn hàng');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error deleting wholesale order:', error);
      setMessage('Có lỗi xảy ra khi xóa đơn hàng: ' + error.message);
      setMessageType('error');
    }
  };

  // Load orders from API
  useEffect(() => {
    const loadOrders = async () => {
      if (!accessToken) {
        console.warn('No access token available for loading wholesale orders');
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        console.log('Loading wholesale orders from API...');

        // Compute month range from "YYYY-MM" (parse explicitly to avoid locale/UTC parsing bugs).
        const [yy, mm] = selectedMonth.split('-').map(Number);
        const pad = (n) => String(n).padStart(2, '0');
        const monthStart = `${yy}-${pad(mm)}-01`;
        const monthEnd = new Date(yy, mm, 0).getDate();
        const effectiveFrom = monthStart;
        const effectiveTo = `${yy}-${pad(mm)}-${pad(monthEnd)}`;

        // type=2 = đơn sỉ (wholesale). API.js đọc startDate/endDate, KHÔNG đọc fromDate/toDate.
        const result = await CungVeDichAPI.getOrders(accessToken, userInfo.id, 2, {
          startDate: effectiveFrom,
          endDate: effectiveTo,
          pageSize: 1000,
          customerId: selectedCustomer
        });

        if (result.success && result.orders) {
          // Transform API data to form format
          console.log('result.orders (wholesale):', result.orders);
          const transformedOrders = result.orders.map(order => ({
            id: order.id,
            customerName: order.customer ? (order.customer.facebook || order.customer.name) : '',
            phoneNumber: order.customer ? order.customer.phone : '',
            quantity: parseInt(order.totalCount) || 0,
            amount: parseFloat(order.totalAmount) || 0,
            createdAt: order.createdAt,
            updatedAt: order.updatedAt
          }));
          console.log('Transformed wholesale orders:', transformedOrders);
          setOrders(transformedOrders);
          console.log('Wholesale orders loaded successfully:', transformedOrders.length);
        } else {
          console.error('Failed to load wholesale orders:', result);
          setMessage('Không thể tải danh sách đơn hàng bán buôn từ server');
          setMessageType('error');
        }
      } catch (error) {
        console.error('Error loading wholesale orders:', error);
        setMessage('Có lỗi xảy ra khi tải danh sách đơn hàng bán buôn: ' + error.message);
        setMessageType('error');
      } finally {
        setInitialLoading(false);
      }
    };

    loadOrders();
  }, [accessToken, userInfo.id, selectedMonth, selectedCustomer]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Danh sách đơn hàng bán buôn"
        pageSubtitle="Xem thông tin đơn hàng bán buôn"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="max-w-4xl mx-auto px-1 sm:px-1 lg:px-1">
        {/* Back button */}
        <div className="flex items-center gap-2 px-3 pt-2 pb-1">
          <button onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
            style={{ color: '#009688', background: '#00968812' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            <span>Quay lại</span>
          </button>
          <GuideButton slug="huong-dan-don-si" />
        </div>
        <div className="bg-white rounded-lg shadow-sm border flex flex-col h-[calc(100vh-240px)]">

          <div className="flex flex-col flex-1">
            {/* Message Display */}
            {message && (
              <div className={`mx-6 mt-6 p-4 rounded-lg flex-shrink-0 ${messageType === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                {message}
              </div>
            )}

            {/* Filters */}
            <div className="px-6 pt-4 flex flex-col md:flex-row items-end md:items-center justify-between gap-4">
              {wholesaleConfig?.showCustomerFilter !== false && (
              <div className="w-full md:w-1/3">
                <AutocompleteSelect
                  options={customers}
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                  placeholder="Lọc theo đại lý..."
                  displayField={(customer) => [customer.code, customer.facebook || customer.name, customer.userName || customer.phone].filter(Boolean).join(' - ')}
                  searchFields={['code', 'facebook', 'name', 'userName', 'phoneNumber', 'phone']}
                  maxResults={10}
                />
              </div>
              )}

              <div className="flex items-center gap-2">
                <label htmlFor="month-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">Tháng:</label>
                <select
                  id="month-select"
                  value={selectedMonth.split('-')[1]}
                  onChange={(e) => {
                    const year = selectedMonth.split('-')[0];
                    setSelectedMonth(`${year}-${e.target.value}`);
                  }}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-1"
                >
                  <option value="01">Tháng 1</option>
                  <option value="02">Tháng 2</option>
                  <option value="03">Tháng 3</option>
                  <option value="04">Tháng 4</option>
                  <option value="05">Tháng 5</option>
                  <option value="06">Tháng 6</option>
                  <option value="07">Tháng 7</option>
                  <option value="08">Tháng 8</option>
                  <option value="09">Tháng 9</option>
                  <option value="10">Tháng 10</option>
                  <option value="11">Tháng 11</option>
                  <option value="12">Tháng 12</option>
                </select>
                <select
                  id="year-select"
                  value={selectedMonth.split('-')[0]}
                  onChange={(e) => {
                    const month = selectedMonth.split('-')[1];
                    setSelectedMonth(`${e.target.value}-${month}`);
                  }}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-1"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>Năm {year}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Summary Statistics - Compact */}
            <div className="py-3 border-b border-gray-200 flex-shrink-0 bg-blue-50">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded p-3 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-gray-500">Tổng số lượng</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {orders.reduce((total, order) => {
                        return total + (parseInt(order.quantity) || 0);
                      }, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="bg-white rounded p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-gray-500">Tổng giá trị</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {orders.reduce((total, order) => {
                        return total + (parseFloat(order.amount) || 0);
                      }, 0).toLocaleString()}₫
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Orders List */}
            <div className="flex-1 overflow-y-auto">
              {initialLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Đang tải danh sách đơn hàng bán buôn...</span>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Không có đơn hàng bán buôn nào</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Tên KH
                          </th>
                          <th className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 w-20">
                            Ngày
                          </th>
                          <th className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 w-16">
                            Số lượng
                          </th>
                          <th className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 w-20">
                            Thành tiền
                          </th>
                          <th className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 w-24">
                            Thao tác
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {orders.map((order, index) => (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-2 py-2">
                              <div className="text-xs font-medium text-gray-900">
                                {order.customerName}
                              </div>
                            </td>
                            <td className="px-1 py-2 text-center text-xs text-gray-600">
                              {formatDate(order.createdAt)}
                            </td>
                            <td className="px-1 py-2 text-center text-xs text-gray-600">
                              {(parseInt(order.quantity) || 0).toLocaleString()}
                            </td>
                            <td className="px-1 py-2 text-center text-xs font-semibold text-blue-700">
                              {formatPrice(order.amount)}₫
                            </td>
                            <td className="px-1 py-2 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handlePreviewOrder(order.id)}
                                  className="text-purple-600 hover:text-purple-800 focus:outline-none"
                                  title="Xem ảnh"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                {/*<button*/}
                                {/*  type="button"*/}
                                {/*  onClick={() => {*/}
                                {/*    const urlParams = new URLSearchParams(window.location.search);*/}
                                {/*    const token = urlParams.get('token');*/}
                                {/*    if (token) {*/}
                                {/*      navigate(`/don-hang-ban-buon/chinh-sua?id=${order.id}&token=${token}`);*/}
                                {/*    } else {*/}
                                {/*      navigate(`/don-hang-ban-buon/chinh-sua?id=${order.id}`);*/}
                                {/*    }*/}
                                {/*  }}*/}
                                {/*  className="text-blue-600 hover:text-blue-800 focus:outline-none"*/}
                                {/*  title="Chỉnh sửa"*/}
                                {/*>*/}
                                {/*  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                                {/*    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />*/}
                                {/*  </svg>*/}
                                {/*</button>*/}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteOrder(order.id, order.customerName)}
                                  className="text-red-600 hover:text-red-800 focus:outline-none"
                                  title="Xóa"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {/* Action Button - Fixed at bottom */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white py-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => {
                    navigate('/don-hang-ban-buon/tao-moi');
                  }}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Tạo đơn hàng bán buôn
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Preview Dialog */}
      <OrderFormPreview
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false);
          setPreviewOrderId(null);
        }}
        orderId={previewOrderId}
        userInfo={userInfo}
      />
    </div>
  );
};

export default WholesaleOrderList;

